/**
 * Per-chain configuration.
 *
 * Only BNB Chain is in scope today, but every chain-specific value lives here rather
 * than being hardcoded at its point of use, so adding a second chain is a data change
 * plus a chain-selector, not an archaeology exercise. This is the "parameterise from
 * day one" requirement from the build brief.
 */

export interface ChainConfig {
  chainId: number
  /** Human name, as shown to users in network prompts. */
  name: string
  /** Name as it should appear in the wallet's own network list. */
  walletChainName: string
  nativeCurrency: { name: string; symbol: string; decimals: number }
  /** Public RPC URLs offered to the wallet when adding the network. */
  publicRpcUrls: string[]
  blockExplorerUrl: string
  blockExplorerName: string
  /** Whether this chain carries real value. Gates the "real funds" warnings. */
  isTestnet: boolean
}

export const BSC_MAINNET: ChainConfig = {
  chainId: 56,
  name: 'BNB Chain',
  walletChainName: 'BNB Smart Chain',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  publicRpcUrls: ['https://bsc-dataseed.bnbchain.org', 'https://bsc-dataseed1.defibit.io'],
  blockExplorerUrl: 'https://bscscan.com',
  blockExplorerName: 'BscScan',
  isTestnet: false,
}

export const BSC_TESTNET: ChainConfig = {
  chainId: 97,
  name: 'BNB Chain Testnet',
  walletChainName: 'BNB Smart Chain Testnet',
  nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
  publicRpcUrls: ['https://data-seed-prebsc-1-s1.binance.org:8545'],
  blockExplorerUrl: 'https://testnet.bscscan.com',
  blockExplorerName: 'BscScan Testnet',
  isTestnet: true,
}

export const SUPPORTED_CHAINS: { [chainId: number]: ChainConfig } = {
  [BSC_MAINNET.chainId]: BSC_MAINNET,
  [BSC_TESTNET.chainId]: BSC_TESTNET,
}

export function getChainConfig(chainId: number | undefined): ChainConfig | undefined {
  return chainId === undefined ? undefined : SUPPORTED_CHAINS[chainId]
}

/** `wallet_addEthereumChain` parameter shape, derived so it can't drift from the config. */
export function toAddEthereumChainParameter(config: ChainConfig) {
  return {
    chainId: `0x${config.chainId.toString(16)}`,
    chainName: config.walletChainName,
    nativeCurrency: config.nativeCurrency,
    rpcUrls: config.publicRpcUrls,
    blockExplorerUrls: [config.blockExplorerUrl],
  }
}
